import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { NWClient } from '../../client/NWClient';
import { DocumentVaultItemModel } from '../../models/document.vault.item.model';
import { revertAll, RootState } from '../../store';

type documentDetailsRequest = {
  id: number;
  isDocumentContract: boolean;
};

export const fetchDocumentDetails = createAsyncThunk(
  'documentDetails/fetch',
  async (request: documentDetailsRequest): Promise<DocumentVaultItemModel | null> => {
    const response: DocumentVaultItemModel | null = await NWClient.get(
      request.isDocumentContract ? 'document-contract' : 'document-upload',
      request.id
    );
    return response;
  }
);

export type DocumentProfileLabelsProp =
  'employees' | 'suppliers' | 'customers' | 'business_profile_contacts';

export const getDocumentProfileLabelsProp = (category?: string): DocumentProfileLabelsProp => {
  switch (category?.toLowerCase()) {
    case 'suppliers':
      return 'suppliers';
    case 'customers':
      return 'customers';
    case 'business':
      return 'business_profile_contacts';
    case 'employment':
    default:
      return 'employees';
  }
};

type DocumentDetailsTagsPayload = {
  du_types?: string[];
  du_workflows?: string[];
  du_label?: string[];
  dc_types?: string[];
  dc_workflows?: string[];
  dc_label?: string[];
};

type DocumentDetailsUpdatePayload = {
  category: string;
  name: string;
  user: number;
  extras: {
    start_date: string | null;
    end_date: string | null;
    is_signed: boolean;
    subscription: boolean;
    frequency: string;
    amount: number;
    currency: string;
  };
} & Partial<Record<DocumentProfileLabelsProp, number[]>>;

export type DocumentDetailsUpdateRequest = {
  id: number;
  isDocumentContract: boolean;
  /** Owner of the document, sent as the `user` field. */
  userId: number;
  /** Document name without the file extension. */
  fileName: string;
  /** 'Business' | 'Customers' | 'Employment' | 'Suppliers' - case insensitive. */
  category: string;
  /** Id of the selected employee/supplier/customer/business contact, as a string. */
  assigneeLabel: string;
  documentTypeLabel: string;
  documentWorkflowLabel: string;
  customLabel: string;
  /** ISO string or 'YYYY-MM-DD'; only the date part is sent. */
  startDate: string | null;
  /** ISO string or 'YYYY-MM-DD'; only the date part is sent. */
  endDate: string | null;
  /** 'true' when the document is signed. */
  signed: string;
  /** 'true' when the document is a subscription. */
  subscription: string;
  frequency: string;
  amount: string;
  currency: string;
};

export type DocumentDetailsUpdateResult = {
  document: DocumentVaultItemModel;
  tags: DocumentDetailsTagsPayload;
};

export const updateDocumentDetails = createAsyncThunk(
  'documentDetails/update',
  async (request: DocumentDetailsUpdateRequest): Promise<DocumentDetailsUpdateResult> => {
    const {
      id,
      isDocumentContract,
      userId,
      fileName,
      category,
      assigneeLabel,
      documentTypeLabel,
      documentWorkflowLabel,
      customLabel,
      startDate,
      endDate,
      signed,
      subscription,
      frequency,
      amount,
      currency,
    } = request;

    const tagsPayload: DocumentDetailsTagsPayload = isDocumentContract
      ? {
          dc_types: documentTypeLabel ? [documentTypeLabel] : [],
          dc_workflows: documentWorkflowLabel ? [documentWorkflowLabel] : [],
          dc_label: customLabel ? [customLabel] : [],
        }
      : {
          du_types: documentTypeLabel ? [documentTypeLabel] : [],
          du_workflows: documentWorkflowLabel ? [documentWorkflowLabel] : [],
          du_label: customLabel ? [customLabel] : [],
        };

    const profilePayload: Partial<Record<DocumentProfileLabelsProp, number[]>> = {
      [getDocumentProfileLabelsProp(category)]: assigneeLabel ? [Number(assigneeLabel)] : [],
    };

    const documentPayload: DocumentDetailsUpdatePayload = {
      ...profilePayload,
      category: category ? category.toLowerCase() : '',
      extras: {
        start_date: startDate ? startDate.substring(0, 10) : null,
        end_date: endDate ? endDate.substring(0, 10) : null,
        is_signed: signed === 'true',
        subscription: subscription === 'true',
        frequency,
        amount: Number(amount),
        currency,
      },
      name: fileName,
      user: userId,
    };

    const [tags, document] = await Promise.all([
      NWClient.patch<DocumentDetailsTagsPayload>(
        isDocumentContract ? 'tag-document-contract' : 'tag-document-upload',
        id,
        tagsPayload
      ),
      NWClient.patch<DocumentDetailsUpdatePayload>(
        `${isDocumentContract ? 'document-contract' : 'document-upload'}/${id}/`,
        null,
        documentPayload
      ),
    ]);

    return {
      tags,
      document: document as unknown as DocumentVaultItemModel,
    };
  }
);

interface DocumentDetailsState {
  data: DocumentVaultItemModel | null;
  status: string;
  updateStatus: string;
  error: string | null;
}

const initialState: DocumentDetailsState = {
  data: null,
  status: 'idle',
  updateStatus: 'idle',
  error: '',
};

const documentDetailsSlice = createSlice({
  name: 'documentDetails',
  initialState,
  reducers: {
    addDocumentUploadHTML: (state, action: { payload: number; type: string }) => {
      state.data?.document_upload_html?.push(action.payload);
    },
    resetDocumentDetails: (state) => {
      state.data = null;
      state.status = 'idle';
      state.updateStatus = 'idle';
      state.error = '';
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchDocumentDetails.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchDocumentDetails.fulfilled, (state, action) => {
        state.data = action.payload;
        state.status = 'succeeded';
      })
      .addCase(fetchDocumentDetails.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || null;
      })
      .addCase(updateDocumentDetails.pending, (state) => {
        state.updateStatus = 'updating';
        state.error = '';
      })
      .addCase(updateDocumentDetails.fulfilled, (state, action) => {
        state.updateStatus = 'succeeded';
        state.data = { ...action.payload.document, ...action.payload.tags };
      })
      .addCase(updateDocumentDetails.rejected, (state, action) => {
        state.updateStatus = 'failed';
        state.error = action.error.message || null;
      })
      .addCase(revertAll, () => initialState);
  },
});
export const { addDocumentUploadHTML, resetDocumentDetails } = documentDetailsSlice.actions;
export default documentDetailsSlice.reducer;
export const selectDocumentDetails = (state: RootState) => state.documentDetails.data;
export const selectDocumentDetailsStatus = (state: RootState) => state.documentDetails.status;
export const selectDocumentDetailsUpdateStatus = (state: RootState) =>
  state.documentDetails.updateStatus;
export const selectDocumentDetailsError = (state: RootState) => state.documentDetails.error;
